import type { ReactNode } from 'react'
import clsx from 'clsx'

import { data } from '../lib/data'
import { signOut } from '../lib/auth/useSession'
import { BUILD_SHA, BUILD_TIME } from '../lib/build-info'

/**
 * The tinted gradient strip every screen opens with (README §Shared shell).
 * `padding:18px 30px 15px` in the general case; Today's own band is
 * `20px 30px 16px`, which is what `tall` selects.
 *
 * The band never scrolls with the screen — it is `flex-none` and the scroll
 * container lives below it.
 */
export function HeaderBand({
  band = 'today',
  tall,
  children,
}: {
  band?: string
  tall?: boolean
  children: ReactNode
}) {
  return (
    <div
      className={[
        'flex flex-none items-center gap-[22px] px-[30px]',
        tall ? 'pb-4 pt-5' : 'pb-[15px] pt-[18px]',
      ].join(' ')}
      style={{ background: `var(--band-${band})` }}
    >
      {children}
    </div>
  )
}

/** The screen name + quiet counts pattern: 600 15px, then 400 11.5px muted, `·` separated. */
export function BandTitle({ name, counts }: { name: string; counts?: string }) {
  return (
    <div className="flex min-w-0 items-baseline gap-[9px] whitespace-nowrap">
      <span className="text-screen font-semibold">{name}</span>
      {counts && <span className="text-[11.5px] font-normal text-muted">{counts}</span>}
    </div>
  )
}

/**
 * THE DATA-MODE BADGE, in the band's right-aligned chip slot — the same
 * geometry as option 3c's `Berkeley · 61°F, clear` chip (7px 12px, radius 8,
 * white on a 1px line, 500 11.5px ink-2), because there is no weather source in
 * this codebase and inventing one would be inventing scope.
 *
 * It exists so nobody ever mistakes the demo dataset for their own life. Quiet
 * on purpose: it is a standing state, not an event, and a badge that shouts
 * gets tuned out on the day it turns into a warning.
 */
export function DataModeBadge({ attention }: { attention?: string | null } = {}) {
  const demo = data.isDemo
  return (
    <div className="ml-auto flex flex-none items-center gap-3">
      <BuildBadge />
      {/* THE DAY PAGE'S DEGRADED STATE (day-page Degraded board): when a source it reads is stale or
          disconnected, the chip says so in the warning set instead of a standing "Synced". */}
      {attention ? (
        <span className="flex items-center gap-[7px] whitespace-nowrap rounded-[8px] border border-warning-line bg-warning-fill px-3 py-[7px] text-[11.5px] font-medium leading-[1.3] text-warning">
          <span className="size-[6px] rounded-full bg-warning-dot" aria-hidden />
          {attention}
        </span>
      ) : (
      <span
        title={
          demo
            ? 'No account is configured, so the app is running on a fixed in-memory dataset. Nothing here is your data. Private workspace — only you, nothing shared.'
            : 'Signed in. Everything you write here is saved to your own database through the API. Private workspace — only you, nothing shared.'
        }
        className="flex items-center gap-[7px] whitespace-nowrap rounded-[8px] border border-line bg-surface px-3 py-[7px] text-[11.5px] font-medium leading-[1.3] text-ink-2"
      >
        <span
          className={['size-[6px] rounded-full', demo ? 'bg-warning-dot' : 'bg-success-dot'].join(' ')}
          aria-hidden
        />
        {demo ? 'Demo data' : 'Synced'}
      </span>
      )}
      {!demo && (
        <button
          type="button"
          onClick={() => void signOut()}
          className="whitespace-nowrap text-[11px] text-microcopy underline underline-offset-2 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink"
        >
          Sign out
        </button>
      )}
    </div>
  )
}

/**
 * THE BUILD BADGE — `v.<sha>`, quiet metadata next to the Synced/Sign out cluster (owner request,
 * 2026-08-26: stale cached builds have been mistaken for broken deploys more than once, and the
 * only fix that scales is reading the running build straight off the screen).
 *
 * `demo mode too` is not a special case here — the SHA and timestamp describe the CODE that is
 * running, which is exactly as true with the in-memory dataset as it is against a real backend.
 *
 * Deliberately as quiet as the settings-nav footnote text (`text-microcopy`, no border, no chip):
 * this is provenance for a bug report, not a status anyone should read on every glance. Clicking
 * copies the full `<sha> <timestamp>` pair — what actually goes in a report — while the visible
 * label stays short enough to sit beside Synced/Sign out without crowding them.
 */
export function BuildBadge({ className }: { className?: string } = {}) {
  return (
    <button
      type="button"
      title={`Built ${BUILD_TIME} — click to copy`}
      onClick={() => {
        void navigator.clipboard?.writeText(`${BUILD_SHA} ${BUILD_TIME}`).catch(() => {
          // Clipboard access can be refused (insecure context, permissions policy). The tooltip
          // still carries the same information, so a failed copy loses convenience, not the fact.
        })
      }}
      className={clsx(
        'whitespace-nowrap text-[11px] font-normal leading-none text-microcopy transition-colors',
        'duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink-2',
        className,
      )}
    >
      v.{BUILD_SHA}
    </button>
  )
}
